import React, {memo, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {IWorkerState} from '../../model/workerState';
import {ControlButton, TscButtonType, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import Constants from '../../constants/Constants';
import cssConstants from '../../constants/cssConstants';
import '../../static/css/modal.less';
import {useTscTheme} from '../../context/themeProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import {StyledWarning} from '../util/StyledElements';
import {useTeamsStore} from '../../context/teamsStore';
import invitationApi, {InvitationStatus} from '../../service/invitationApi';
import {useShallow} from 'zustand/react/shallow';

const BoldText = styled.span`
   font-weight: ${TscStyles.font.weight.semibold};
   display: inline-flex;
   word-break: break-word;
`;

const TeamInvitationBody = styled.div`
   margin-bottom: 1.625rem;
   font-weight: ${TscStyles.font.weight.regular};
`;

export interface IAcceptTeamInvitationModalState extends IWorkerState {
   errorText: string;
}

export const AcceptTeamInvitationModalBase: React.FC<IAcceptTeamInvitationModalProps> = ({onClose, teamId, teamName, invitationId, invitationSender}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [sender, setSender] = useState(invitationSender);
   const [modalState, setModalState] = useState<IAcceptTeamInvitationModalState>({
      isWorking: false,
      hasError: false,
      errorText: ''
   });
   const {teamInvitations} = useTeamsStore(useShallow(s => ({teamInvitations: s.teamInvitations})));

   useEffect(() => {
      if (!invitationSender) {
         setSender(teamInvitations?.data?.find(i => i.invitationId === invitationId)?.creator?.DisplayName);
      }
   }, [invitationSender]);

   const acceptInvitation = async () => {
      try {
         setModalState({
            ...modalState,
            isWorking: true
         });
         await invitationApi.updateInvitationStatus(invitationId, InvitationStatus.Accepted);
         window.location.href = `${Constants.navigation.teamsLink}/${teamId}`;
      } catch (exception) {
         setModalState({
            isWorking: false,
            hasError: true,
            errorText: t('team.modal.inviteNotification.acceptErrorText')
         });
      }
   };

   const declineInvitation = async () => {
      try {
         setModalState({
            ...modalState,
            isWorking: true
         });
         await invitationApi.updateInvitationStatus(invitationId, InvitationStatus.Rejected);
         window.location.reload();
         onClose();
      } catch (exception) {
         setModalState({
            isWorking: false,
            hasError: true,
            errorText: t('team.modal.inviteNotification.declineErrorText')
         });
      }
   };

   return (
      <BasicModalPortal
         testId="team-invitation-modal"
         title={t('team.modal.inviteNotification.title', {teamName})}
         width={cssConstants.acceptTeamInvitationModalWidth}
         isLoading={modalState.isWorking}
         themeName={theme}
         onClose={onClose}
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               <div className="form-field-group">
                  <TeamInvitationBody>
                     <Trans
                        i18nKey="team.modal.inviteNotification.body"
                        default={'{{sender}} has invited you to join the team <strong>\'{{teamName}}\'</strong>. Would you like to accept the invitation?'}
                        values={{sender: sender, teamName: teamName}}
                        components={{strong: <BoldText />}}
                     />
                     {modalState.hasError && modalState.errorText && (
                        <StyledWarning id="error-message">
                           {modalState.errorText}
                        </StyledWarning>
                     )}
                  </TeamInvitationBody>
               </div>

               <div className="button-group">
                  <ControlButton
                     label={t('general.close')}
                     buttonType={TscButtonType.tertiary}
                     themeName={theme}
                     onClick={onClose}
                     testId="team-invitation-close-button"
                     width="5.9375rem"
                  />
                  <ControlButton
                     label={t('team.modal.inviteNotification.decline')}
                     buttonType={TscButtonType.secondary}
                     themeName={theme}
                     onClick={declineInvitation}
                     testId="team-invitation-decline-button"
                     width="5.9375rem"
                  />
                  <ControlButton
                     label={t('team.modal.inviteNotification.accept')}
                     buttonType={TscButtonType.primary}
                     themeName={theme}
                     onClick={acceptInvitation}
                     testId="team-invitation-accept-button"
                     width="5.9375rem"
                  />
               </div>
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface IAcceptTeamInvitationModalProps {
   onClose(): void;
   teamId: string;
   teamName: string;
   invitationId: string;
   invitationSender?: string;
}

export default memo(AcceptTeamInvitationModalBase);